import React, { useState, useEffect } from 'react';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { DollarSign, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { showSuccess, showError } from '../../utils/swal';
import { useNavigate } from 'react-router-dom';

const PersonnelCommissions = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        stats: {
            total: 0,
            received: 0,
            pending: 0,
            overdue: 0
        },
        commissions: []
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchCommissions();
    }, []);

    const fetchCommissions = async () => {
        try {
            const response = await api.get('/commissions');
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Komisyonlar yüklenemedi:', error);
            showError('Komisyonlar yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (commissionId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'pending' ? 'received' : 'pending';
            const response = await api.put(`/commissions/${commissionId}/status`, { status: newStatus });
            if (response.data.success) {
                showSuccess('Durum güncellendi');
                fetchCommissions();
            }
        } catch (error) {
            console.error('Durum güncellenemedi:', error);
            showError(error.response?.data?.message || 'Durum güncellenirken bir hata oluştu');
        }
    };

    const isOverdue = (commission) => {
        if (commission.status !== 'pending' || !commission.payment_date) return false;
        return new Date(commission.payment_date) < new Date();
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;
    }

    const StatCard = ({ title, amount, icon: Icon, colorClass }) => (
        <Card className={`relative overflow-hidden`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{title}</p>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {parseFloat(amount).toLocaleString('tr-TR')} ₺
                    </h3>
                </div>
                <div className={`p-3 rounded-xl ${colorClass}`}>
                    <Icon size={24} />
                </div>
            </div>
            <div className="absolute -right-2 -bottom-2 opacity-5">
                <Icon size={80} />
            </div>
        </Card>
    );

    const columns = [
        {
            header: 'Müşteri',
            render: (row) => (
                <span
                    className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (row.customer?.id) navigate(`/customers/${row.customer.id}`);
                    }}
                >
                    {row.customer?.name} {row.customer?.surname}
                </span>
            )
        },
        {
            header: 'Portföy',
            render: (row) => (
                <span
                    className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (row.portfolio?.id) navigate(`/portfolios/${row.portfolio.id}`);
                    }}
                >
                    {row.portfolio?.title}
                </span>
            )
        },
        {
            header: 'İşlem Tipi',
            render: (row) => (
                <span className={`px-2 py-1 rounded-md text-[10px] uppercase font-bold tracking-wider ${row.source_type === 'sale'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
                    }`}>
                    {row.source_type === 'sale' ? 'Satış' : 'Kiralama'}
                </span>
            )
        },
        {
            header: 'K. Tutarı',
            render: (row) => (
                <div className="font-bold text-gray-900 dark:text-white">
                    {parseFloat(row.commission_amount).toLocaleString('tr-TR')} ₺
                </div>
            )
        },
        {
            header: 'Ödeme Tarihi',
            render: (row) => (
                <div className="text-gray-600 dark:text-gray-400">
                    {row.payment_date ? new Date(row.payment_date).toLocaleDateString('tr-TR') : '-'}
                </div>
            )
        },
        {
            header: 'Durum',
            render: (row) => {
                const overdue = isOverdue(row);
                
                if (row.status === 'cancelled') {
                    return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                            İptal Edildi
                        </span>
                    );
                }

                return (
                    <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${row.status === 'received'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}>
                            {row.status === 'received' ? 'Alındı' : 'Bekliyor'}
                        </span>
                        {overdue && (
                            <span className="flex items-center px-2 py-1 rounded-md text-[10px] font-bold bg-red-500 text-white animate-pulse">
                                <AlertCircle size={10} className="mr-1" />
                                GECİKMİŞ
                            </span>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Komisyonlarım</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">İşlemlerinizden hak ettiğiniz komisyonların takibi</p>
                </div>
            </div>

            {/* İstatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Toplam Komisyon"
                    amount={data.stats.total}
                    icon={TrendingUp}
                    colorClass="bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
                />
                <StatCard
                    title="Alınan Komisyon"
                    amount={data.stats.received}
                    icon={CheckCircle}
                    colorClass="bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300"
                />
                <StatCard
                    title="Bekleyen Komisyon"
                    amount={data.stats.pending}
                    icon={Clock}
                    colorClass="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-300"
                />
                <StatCard
                    title="Gecikmiş Komisyon"
                    amount={data.stats.overdue}
                    icon={AlertCircle}
                    colorClass="bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300"
                />
            </div>

            {/* Liste */}
            <Card noPadding>
                <Table
                    columns={columns}
                    data={data.commissions}
                    loading={loading}

                    customActions={(row) => (
                        row.status !== 'cancelled' && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusToggle(row.id, row.status);
                                }}
                                className={`p-2 rounded-lg transition-all duration-200 ${row.status === 'received'
                                    ? 'text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20'
                                    : 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                                    }`}
                                title={row.status === 'received' ? 'Bekliyor Olarak İşaretle' : 'Alındı Olarak İşaretle'}
                            >
                                {row.status === 'received' ? <Clock size={16} /> : <CheckCircle size={16} />}
                            </button>
                        )
                    )}
                />
            </Card>
        </div>
    );
};

export default PersonnelCommissions;
